/**
 * Paging for lists of records (CLAUDE.md "Scale").
 *
 * WHY. Reading every record works on a demo and fails on the busiest customer.
 * A list asks for one page at a time and is told where the next one starts;
 * nothing in the app can ask the database for "everything".
 *
 * Keyset paging, not offset: the cursor is the last row's sort value (usually
 * `createdAt`) and `id`, so a record created while someone is reading never
 * shifts the page under them and never hides a row.
 */
import { z } from 'zod'
import { itemSchema } from './item.ts'

/** A page, when nobody says otherwise. */
export const DEFAULT_PAGE_SIZE = 50
/** The most any one request may take, however it asks. */
export const MAX_PAGE_SIZE = 200

/**
 * The most records a whole-collection read may scan — searching, counting.
 * Bounded so one big customer can't stall the server; answers built on it say
 * when they were cut short. Beyond this, a project needs real database-side
 * search and counting (see docs/TEMPLATE.md "What to check before a project
 * goes live").
 */
export const SCAN_LIMIT = 1000

export const pageInputSchema = z.object({
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE).optional(),
  /** From the previous page's `nextCursor`. */
  cursor: z.string().nullish(),
})
export type PageInput = z.infer<typeof pageInputSchema>

export const itemPageSchema = z.object({
  items: z.array(itemSchema),
  /** Pass back as `cursor` for the next page. Null when this is the last one. */
  nextCursor: z.string().nullable(),
})
export type ItemPage = z.infer<typeof itemPageSchema>

/**
 * `<value>|<id>` — the position of the last row on a page. `value` is
 * whatever the caller is sorting by (a `createdAt` or `deletedAt`), as a
 * string — NOT necessarily what `Item.createdAt` holds. The Drizzle
 * repository encodes the database's own full-precision text for it rather
 * than the millisecond-precision ISO string the API returns, so a row can't
 * fall between two pages when two records share a millisecond
 * (item.repository.drizzle.ts, `readPage`).
 */
export const encodeCursor = (row: { value: string; id: string }) => `${row.value}|${row.id}`
export function decodeCursor(cursor: string | null | undefined) {
  if (!cursor) return null
  // At the LAST `|`: an id never holds one, but the sort value may (a client
  // named "A|B") — splitting at the first sent the next page back to the start.
  const cut = cursor.lastIndexOf('|')
  if (cut < 0) return null
  const value = cursor.slice(0, cut)
  const id = cursor.slice(cut + 1)
  return value && id ? { value, id } : null
}

/** Paging one already-loaded array — the in-memory repositories. */
export function pageOf<T extends { id: string; createdAt: string }>(
  rows: readonly T[],
  page: PageInput | undefined,
  sortBy: (row: T) => string = (row) => row.createdAt,
): { items: T[]; nextCursor: string | null } {
  const limit = Math.min(page?.limit ?? DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE)
  const sorted = [...rows].sort(
    (a, b) => sortBy(b).localeCompare(sortBy(a)) || b.id.localeCompare(a.id),
  )
  const after = decodeCursor(page?.cursor)
  // The first row past the cursor's position, as Postgres's keyset reads it —
  // not the row it names, which may have been deleted since (then a search for
  // it found nothing and the next page started over, for ever).
  const pastCursor = (row: T) => {
    if (!after) return true
    const byValue = after.value.localeCompare(sortBy(row))
    return byValue > 0 || (byValue === 0 && after.id.localeCompare(row.id) > 0)
  }
  const first = sorted.findIndex(pastCursor)
  const start = first < 0 ? sorted.length : first
  const items = sorted.slice(start, start + limit)
  const last = items.at(-1)
  const more = start + items.length < sorted.length
  return {
    items,
    nextCursor: more && last ? `${sortBy(last)}|${last.id}` : null,
  }
}

/**
 * Read up to `limit` records, a page at a time, from a keyset-paged list —
 * what "scan the newest SCAN_LIMIT" means when one page holds at most
 * MAX_PAGE_SIZE. `more` says records were left unread, so an answer built on
 * them can say it is a floor. Asking a repository for `limit` in one page
 * would silently get MAX_PAGE_SIZE instead.
 */
export async function scanPages<T>(
  read: (page: PageInput) => Promise<{ items: T[]; nextCursor: string | null }>,
  limit: number = SCAN_LIMIT,
): Promise<{ items: T[]; more: boolean }> {
  const items: T[] = []
  let cursor: string | null = null
  do {
    const page = await read({ limit: Math.min(MAX_PAGE_SIZE, limit - items.length), cursor })
    items.push(...page.items)
    cursor = page.nextCursor
  } while (cursor !== null && items.length < limit)
  return { items, more: cursor !== null }
}
